/**
 * Receiver transfer host — the pure per-transfer orchestration the offscreen
 * document runs once the user accepts an inbound file offer
 * (docs/file-transfer-resume.md §4.2/§6; TASK-17 brief). Mirrors
 * `sidepanel/filetransfer/sendFile.ts`'s role for the sender: every side
 * effect (OPFS output, HTTP, crypto, ctrl messaging, `browser.downloads`) is
 * injected via `ReceiveHostDeps`, so this module is fully unit-testable with
 * fakes (see `__tests__/receiveHost.test.ts`) — no `browser.*`/OPFS/network
 * here. The REAL wiring lives in the sibling `receiveDeps.ext.ts` adapter.
 *
 * Flow (docs/file-transfer-resume.md §4.2 P-R1..P-R4, §9.2; docs/file-transfer-p2p.md §16 pt.4):
 *   1. Allocate the OPFS output file (`output.open()`) — the destination
 *      MUST exist before the accept ctrl is sent (accept-before-download
 *      ordering, TASK-17 brief's "Controller resolutions" — mirrors
 *      Android's shipped `FileTransferCoordinator.accept()`).
 *   2. `onOutputAllocated` hook (TASK-15 seam: P-R1 persist — "Full record
 *      ... written BEFORE sending the accept ctrl").
 *   3. Send `accept{transferId}`.
 *   4. Re-derive `transferKey` (never persisted, §2.5). If `deps.p2p` is
 *      eligible (`FT_P2P_ENABLED`, Web Lock held), attempt a P2P promotion
 *      BEFORE the relay download loop — one of four outcomes:
 *        - "not-attempted" (no `deps.p2p` / ineligible / `connect()` -> null):
 *          falls through to step 5 unmodified (byte-identical M2 behavior).
 *        - "demoted": send the `demote{lastVerifiedChunk}` ctrl, RELEASE this
 *          fresh receive's OPFS worker (`output.release?.()` — its exclusive
 *          sync-access-handle lock would otherwise deadlock the resume
 *          handoff's brand-new worker on the SAME file), then hand off
 *          DIRECTLY to `receiveResume.ts`'s `reconcileAndContinue` and return
 *          early — steps 5-6 below never run for this receive.
 *        - "hashMismatch": throw `HashMismatchError`, running step 5's
 *          existing mismatch catch verbatim (the P2P attempt already closed
 *          the channel without a COMPLETE frame).
 *        - "completed": the P2P attempt already sent the in-band COMPLETE
 *          frame and verified the hash itself — skip straight to step 6 (this
 *          path never opens a second worker, so no release is needed).
 *   5. Run the ordered download loop (`receiveLoop.ts`'s `runReceiveLoop` —
 *      the "one code path" TASK-15's resume reuses verbatim), then the
 *      whole-file SHA-256 verify. Mismatch -> `HASH_MISMATCH` error ctrl +
 *      delete output + terminal "failed" (M1 policy kept, §9.2).
 *   6. Match -> `complete{verified:true}` ctrl + terminal "complete", THEN
 *      `output.finalize()` -> `downloads.download()` -> delete the OPFS
 *      copy ONLY after the download handoff succeeds (TASK-17 acceptance:
 *      "OPFS copy removed after successful download handoff").
 *
 * M1-mirrored error semantics (Android `Receiver.kt`/`FileTransferCoordinator`,
 * kept verbatim): every non-success outcome deletes the OPFS output. Chunk-auth
 * failure -> `CHUNK_AUTH_FAIL` (retry-once already exhausted inside
 * `receiveLoop.ts`); anything else -> `STORAGE_FULL` (a real
 * `QuotaExceededError` surfaced by the output layer) or `INTERNAL` otherwise.
 * The receiver NEVER calls `DELETE /v1/transfers/:id` — that registration is
 * sender-owned (docs/file-transfer-resume.md §8.2 vs §8.3).
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import type { FileCtrl, TransferManifest } from "@reflecto/core";
import { ChunkAuthError, decodeBase64, FT_P2P_ENABLED } from "@reflecto/core";
import { isDefinitive404, isNetworkError } from "./errors";
import { type FinalizedFile, rewrapFinalizedFile } from "./fileSeam";
import type { FtTransport, P2pReceiveOutcome, ReceiverP2pDeps } from "./p2pTypes";
import { partPlan } from "./partPlan";
import { createThrottledProgressSender } from "./progressThrottle";
import {
  HashMismatchError,
  ReceiveAbortedError,
  type ReceiveHandoffDisposition,
  type ReceiveLoopCrypto,
  type ReceiveLoopHash,
  type ReceiveLoopOutput,
  runReceiveLoop,
} from "./receiveLoop";
import { reconcileAndContinue as reconcileReceiverAndContinue } from "./receiveResume";
import {
  FT_POLL_ACTIVE_MS,
  FT_POLL_IDLE_AFTER_MS,
  FT_POLL_IDLE_MS,
  FT_RECEIVE_LIVENESS_MS,
} from "./state";
import { isQuotaExceededError } from "./workerError";

/** The OPFS-backed receiver output (TASK-17's opfsWriter worker seam). Truncate/readRange are unused by this M2 fresh-receive-only host but are part of the contract from day one — TASK-15's resume driver calls them through the SAME worker client with no protocol change. */
export interface ReceiverOutput extends ReceiveLoopOutput {
  /** Create (or truncate-open) the OPFS output file — the P-R1 "destination exists" step. */
  open(): Promise<void>;
  /** TASK-15 resume seam (§6.2 step 3) — truncate to a part boundary. Unused by M2. */
  truncate(size: number): Promise<void>;
  /** TASK-15 resume seam (§6.2 step 5's digest rebuild) — read back `[start, end)`. Unused by M2. */
  readRange(start: number, end: number): Promise<Uint8Array>;
  /** Current byte size, or `null` if the file doesn't exist — mirrors `SenderSource.getSize` (TASK-15's output check, §6.2 step 1). Unused by M2. */
  getSize(): Promise<number | null>;
  /** Release the sync access handle and hand back the finished file for the downloads handoff. A browser/OPFS adapter returns a real `File`; a path-based adapter (the CLI) returns a `FinalizedFileCarrier` — see [FinalizedFile]. */
  finalize(): Promise<FinalizedFile>;
  /** Delete the OPFS copy — terminal cleanup (P-R4) or post-handoff cleanup. */
  deleteCopy(): Promise<void>;
  /**
   * Release the exclusive sync-access-handle lock (and terminate the underlying worker) WITHOUT
   * finalizing or reading back the file — the P2P demotion release valve (docs/file-transfer-p2p.md
   * §16 pt.4, MUST-2 review fix). `receiveHost.ts` calls this BEFORE handing off to
   * `reconcileAndContinue`, whose `openOutput()` spins up a BRAND-NEW worker on the SAME OPFS
   * file — without releasing this worker's exclusive `createSyncAccessHandle()` lock first, the
   * new worker's `open()` deadlocks against it, and the resulting failure is caught by the
   * generic INTERNAL branch below, which deletes the (already-persisted) partial file. Optional
   * so every OTHER `ReceiverOutput` fake (fresh-receive tests that never demote) is unaffected —
   * the REAL wiring (`receiverWorkerClient.ts`) always provides it. Idempotent — safe to call
   * more than once, and safe alongside a later `dispose()`/`terminate()` from the same worker
   * client (mirrors `offscreen/index.ts`'s `dispose()` idempotence model).
   */
  release?(): Promise<void>;
}

export interface ReceiveHostCrypto extends ReceiveLoopCrypto {
  deriveTransferKey(
    sharedSecret: Uint8Array,
    transferId: string,
    senderPub: Uint8Array,
    recipientPub: Uint8Array
  ): Promise<Uint8Array>;
}

export interface ReceiveHostHash extends ReceiveLoopHash {
  init(): unknown | Promise<unknown>;
  final(state: unknown): Uint8Array;
}

export interface ReceiveHostApi {
  /**
   * Availability poll (`GET /v1/transfers/:id`) — only `partsUploaded` is consumed. The fresh
   * relay loop gates every `getDownloadUrl` call on the part actually appearing here first
   * (§6.2 step 7 semantics, same contract the resume reconciler follows): the sender only
   * STARTS uploading after our accept ctrl reaches it, so asking for a part's download-url
   * immediately after accept deterministically answers 409 `part_not_uploaded` — which is
   * exactly how every fresh phone→web transfer died in prod (2026-08-16).
   */
  getTransfer(transferId: string): Promise<{ partsUploaded: number[] }>;
  /** A fresh presigned GET URL for one part (`GET /v1/transfers/:id/parts/:n/download-url`). */
  getDownloadUrl(transferId: string, partIndex: number): Promise<{ url: string }>;
  /** Open the presigned URL as a ciphertext stream — never buffers the whole response. */
  fetchPartStream(url: string): Promise<ReadableStream<Uint8Array>>;
}

/**
 * The fresh-path analogue of `receiveResume.ts`'s #821 peer-liveness give-up: thrown when
 * `FT_RECEIVE_LIVENESS_MS` elapses with NO new part appearing in `partsUploaded`. Unlike the
 * resume path's stop-signal (whose record a later scan retries), the fresh path is a live
 * session with no guaranteed scan behind it, so this maps to the ordinary terminal-failure
 * branch (INTERNAL wire code — the peer, if merely suspended, can re-offer).
 */
export class PartWaitStalledError extends Error {
  constructor() {
    super("no new part appeared within the liveness window");
    this.name = "PartWaitStalledError";
  }
}

/** The finished OPFS output handed off to the OS. Returns whether the handoff is CONFIRMED complete (`"durable"`) or merely started (`"detached"`) — the core deletes the staged copy only for `"durable"`, because deleting under an in-flight read invalidates it. */
export interface ReceiveDownloads {
  download(file: FinalizedFile, suggestedName: string): Promise<ReceiveHandoffDisposition>;
}

export interface ReceiveHostDeps {
  api: ReceiveHostApi;
  /** Single-file back-compat output — used verbatim (byte-identical) when `openOutput` is absent, i.e. every pre-multi-file caller. */
  output: ReceiverOutput;
  /**
   * Multi-file output seam (multi-file transfer design §6,
   * docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md) — resolves file
   * `fileIndex`'s OWN output handle. Optional: absent means every file uses `output` above
   * (the single-file case, byte-identical to pre-multi-file behavior — real single-file
   * callers never set this).
   */
  openOutput?(fileIndex: number): ReceiverOutput;
  crypto: ReceiveHostCrypto;
  hash: ReceiveHostHash;
  downloads: ReceiveDownloads;
  /** Encrypt-then-POST a file_ctrl envelope to the transfer's sender (background/fileCtrl.ts's sendFileCtrl, relayed via the SW). */
  sendCtrl(ctrl: FileCtrl): Promise<void>;
  /** Raw X25519 shared secret (`crypto_scalarmult` output) for (own secret key, sender's pub) — re-derived, never persisted (§2.5). */
  sharedSecret: Uint8Array;
  /** The transfer's SENDER's public key (the peer) — crypto-spec.md §3's fixed role order; NOT swapped on the receiver side. */
  senderPublicKey: Uint8Array;
  /** OUR OWN (recipient) public key — crypto-spec.md §3's fixed role order. */
  ownPublicKey: Uint8Array;
  /** P-R1 persist seam (TASK-15): called once the output exists, BEFORE the accept ctrl is sent. */
  onOutputAllocated?(): Promise<void>;
  /** P-R2 persist seam (TASK-15): see receiveLoop.ts's `onPartWritten`. */
  onPartWritten?(partIndex: number, chunksVerified: number): Promise<void>;
  /** P-R4 persist seam (TASK-15): called with the terminal status, BEFORE artifact cleanup — mirrors senderResume.ts's `persistTerminal`. */
  onTerminal?(
    status: "complete" | "failed" | "cancelled",
    errorCode?: string,
    /** Which path carried the bytes — see [FtTransport]. Not reconstructable after the fact. */
    transport?: FtTransport
  ): Promise<void>;
  onProgress?(partIndex: number, partCount: number): void;
  /** Cooperative cancel/unpair check, polled between parts (see receiveLoop.ts's module doc for why this granularity is sufficient). */
  isAborted?(): boolean;
  /** Clock seam for the availability gate's cadence/liveness math (tests) — defaults to `Date.now`. Mirrors receiveResume.ts's `nowOf`. */
  now?(): number;
  /** Sleep seam for the availability gate's poll cadence (tests) — defaults to a real `setTimeout`. Mirrors receiveResume.ts's `sleepOf`. */
  sleep?(ms: number): Promise<void>;
  /**
   * P2P promotion seam (docs/file-transfer-p2p.md §16 pt.4) — optional; ABSENT means
   * byte-identical M2 behavior (every existing test still passes unmodified). Eligibility here is
   * JUST `FT_P2P_ENABLED` (no `resumeFromChunk`-style gate — `receiveFile` is ALWAYS the
   * fresh-accept path, so this structurally satisfies "P2P only on the initial live session",
   * §1.6: `ReceiveResumeDeps` never gains a `p2p` field, so a resume can never re-promote).
   */
  p2p?: ReceiverP2pDeps;
  /**
   * TASK-30 — UI-only hook, fired exactly once by a live P2P attempt at the CONNECTING_P2P ->
   * TRANSFERRING_P2P transition (see `P2pReceiveAttemptArgs.onTransferring`'s doc in
   * `p2pTypes.ts`). Passed straight through to `deps.p2p.attempt()` — `receiveHost.ts` itself never
   * interprets it, purely plumbing (mirrors `onPartWritten`).
   */
  onTransferring?(): void;
}

/**
 * Run one accepted receive to completion (or a terminal failure/cancellation).
 * `manifest` is the already-validated offer manifest (the SW's
 * `dispatchIncomingFileCtrl` validates every inbound offer before it ever
 * reaches a pending-offer registry, §fileCtrl.ts).
 */
export async function receiveFile(
  manifest: TransferManifest,
  deps: ReceiveHostDeps
): Promise<void> {
  const file = manifest.files[0]; // the P2P-eligible (single-file-only) case's sole file
  const transferId = manifest.transferId;
  // File-aligned global-part -> per-file chunk-range map (multi-file transfer design §4).
  // The degenerate single-file plan is byte-identical to the pre-multi-file
  // `Math.ceil(file.chunkCount / CHUNKS_PER_PART)` math.
  const plan = partPlan(manifest.files);
  const partCount = plan.length;

  // One output handle per file — `outputFor(i)` resolves via the optional multi-file
  // `openOutput` seam, falling back to the single back-compat `output` when absent (every
  // pre-multi-file caller). Built as a fixed array so `outputFor` is a stable, memoized
  // lookup rather than re-invoking `deps.openOutput` per call.
  const outputs: ReceiverOutput[] = manifest.files.map((_f, i) =>
    deps.openOutput ? deps.openOutput(i) : deps.output
  );
  const outputFor = (fileIndex: number): ReceiverOutput => outputs[fileIndex];
  const deleteAllOutputs = async (): Promise<void> => {
    await Promise.all(outputs.map((o) => o.deleteCopy().catch(() => {})));
  };

  // P-R1: every file's destination exists BEFORE the accept ctrl (accept-before-download
  // ordering). Deliberately OUTSIDE the try/catch below — if any file's `open()` fails, no
  // accept ctrl was ever sent, so there is no peer to notify (mirrors the sender's "quota
  // preflight failure -> no ctrl, local failure only" rule). KNOWN GAP: if file i's open()
  // fails after files [0, i) already opened, those earlier OPFS artifacts are not cleaned up
  // here — mirrors the single-file "nothing to clean up on an open failure" invariant
  // (asserted by receiveHost.test.ts) rather than inventing new multi-file cleanup semantics.
  // EVERYTHING from here on (persist hook, accept ctrl, key derivation, the
  // download loop, and the hash verify) runs inside ONE try/catch so every
  // failure path — including a `sendCtrl`/`deriveTransferKey` throw, not
  // just a `runReceiveLoop` failure — deletes every file's OPFS output (M1 mirrored
  // semantics: "every non-success outcome deletes the OPFS output").
  for (const o of outputs) await o.open();

  // §3/§16 pt.4 — acquire the SAME Web Lock `receiveResume.ts`'s resume algorithm uses, right
  // after `output.open()`, BEFORE the accept ctrl — held for this fresh receive's WHOLE lifetime
  // (released in the outer `finally` below). Mirrors the sender's reasoning (`sendFile.ts`):
  // closes, for free, any race between this live fresh receive and a concurrent resume scan for
  // the same transferId. Eligibility here is `FT_P2P_ENABLED` AND `files.length === 1` (multi-file
  // transfer design §8a — the P2P chunk loop still speaks single-file global chunk indexing;
  // multi-file P2P is a later per-surface slice, §4a) — `receiveFile` is ALWAYS the fresh-accept
  // path (§3), so no `resumeFromChunk`-style gate is needed.
  const p2pEligible = deps.p2p !== undefined && FT_P2P_ENABLED && manifest.files.length === 1;
  const p2pLockHeld = p2pEligible && deps.p2p ? await deps.p2p.acquireLock(transferId) : false;

  // Transport-attempt seam (docs/file-transfer-p2p.md §16 pt.4). "not-attempted" (the default: no
  // deps.p2p, ineligible, or connect() returned null) falls through to the UNCHANGED M2 relay
  // loop below, byte-identical to every existing test's expectations. Declared OUTSIDE the inner
  // try/catch below so the tail (after that block) can still read it — needed for the §9.1
  // ordering fix: a "completed" outcome's `closeChannel()` is invoked from the tail, AFTER the
  // wire ctrl + P-R4 persist, never before (see `P2pReceiveOutcome`'s doc).
  let p2pOutcome: P2pReceiveOutcome = { kind: "not-attempted" };
  // Captured as the attempt resolves, because every terminal branch below runs
  // AFTER the surface has cleared its P2P registry (see [FtTransport]). The
  // demoted path never reaches these terminals — it hands off to
  // `reconcileAndContinue`, which reports `relay_after_demote` itself.
  let transport: FtTransport = "relay";

  try {
    try {
      await deps.onOutputAllocated?.();
      await deps.sendCtrl({ kind: "accept", transferId });

      const transferKey = await deps.crypto.deriveTransferKey(
        deps.sharedSecret,
        transferId,
        deps.senderPublicKey, // crypto-spec.md §3 fixed role order — sender's pub
        deps.ownPublicKey // ... recipient's (our own) pub
      );
      const noncePrefixBytes = decodeBase64(manifest.noncePrefix);
      // One PERSISTENT streaming hash state per file (pre-created before the loop —
      // `hashStateFor(i)` below is a stable lookup into this array, never re-`init()`'d).
      const hashStates: unknown[] = [];
      for (const _f of manifest.files) hashStates.push(await deps.hash.init());
      const hashStateFor = (fileIndex: number): unknown => hashStates[fileIndex];

      // The receiver's throttled WIRE progress ctrl (§8.2/§9.2) — best-effort,
      // fire-and-forget; local (own-side) `onProgress` above stays untouched
      // and unthrottled. Suppressed entirely while a P2P attempt is live (§5.3:
      // "no wire progress ctrls are emitted while in a P2P state") — only ever
      // wired into the (possibly P2P-skipped) relay loop call below.
      // KNOWN GAP (multi-file, engine-only this slice): `chunksDone` is the GLOBAL
      // cross-file cursor `runReceiveLoop` reports (see receiveLoop.ts's module doc),
      // but the wire `progress` ctrl is fileId-scoped — always tagged with file 0's id
      // here. Unreachable in production today (the offer-acceptance gate still rejects
      // N-file manifests, per the design doc's slice plan), and byte-identical to today
      // for the only reachable case, `files.length === 1`. Per-file wire progress
      // multiplexing for real N-file transfers is later (UI/wire) slice work.
      const sendProgressCtrl = createThrottledProgressSender((chunksDone) => {
        void deps
          .sendCtrl({ kind: "progress", transferId, fileId: file.fileId, chunksDone })
          .catch(() => {});
      });

      if (p2pLockHeld && deps.p2p) {
        p2pOutcome = await deps.p2p.attempt({
          transferId,
          fileId: file.fileId,
          chunkCount: file.chunkCount,
          chunkSize: manifest.chunkSize,
          fileSize: file.size,
          expectedSha256: file.sha256,
          output: outputFor(0),
          crypto: deps.crypto,
          hash: deps.hash,
          hashState: hashStateFor(0),
          transferKey,
          noncePrefix: noncePrefixBytes,
          onPartWritten: deps.onPartWritten,
          onTransferring: deps.onTransferring,
          // §10.1/§10.2/§11.2 cancel rows — the SAME hook the relay download loop below
          // already carries, so both transports honor an inbound cancel identically.
          isAborted: deps.isAborted,
        });

        if (p2pOutcome.kind === "not-attempted" && p2pOutcome.reason === "connect_failed") {
          // A connect that never opened is NOT a demote in the protocol sense (no
          // channel, so no demote ctrl) — but downstream it is the same question:
          // "why did this end up on the relay?" Without it, a P2P peer that can
          // never connect is indistinguishable from one that has no P2P at all.
          try {
            deps.p2p?.onDemote?.({
              trigger: "connect_failed",
              lastVerifiedChunk: 0,
              chunkCount: file.chunkCount,
            });
          } catch {
            // never let observability break a transfer
          }
        }

        if (p2pOutcome.kind === "demoted") {
          // Analytics seam (#808 follow-up) — exactly once per attempt, fail-open:
          // a throw here would abort a relay fallback that is about to succeed.
          try {
            deps.p2p?.onDemote?.({
              trigger: p2pOutcome.trigger,
              lastVerifiedChunk: p2pOutcome.lastVerifiedChunk,
              chunkCount: file.chunkCount,
            });
          } catch {
            // never let observability break a transfer
          }
          // §8.2 step 3 — best-effort, exactly ONE send attempt, no retry loop.
          await deps
            .sendCtrl({
              kind: "demote",
              transferId,
              lastVerifiedChunk: p2pOutcome.lastVerifiedChunk,
            })
            .catch(() => {});
          // MUST-2 review fix (docs/file-transfer-p2p.md §16 pt.4): release THIS worker's
          // exclusive sync-access-handle lock BEFORE the reconcile handoff below opens a
          // BRAND-NEW worker on the SAME OPFS file. `p2pOutcome.lastVerifiedChunk` is already
          // flushed+persisted by this point — the P2P intake pipeline always calls
          // `onPartWritten` (and `output.flush()`) strictly before resolving a "demoted" outcome
          // (see p2pReceiverAttempt.ts's module doc, "persist-before-ack") — so releasing here
          // loses nothing. Without this, the second worker's `open()` deadlocks against this
          // one's still-held lock, and the resulting throw is caught by the generic INTERNAL
          // branch below, which deletes the (correctly persisted) partial file — hard-failing
          // every real-world demotion.
          await outputFor(0).release?.();
          // §6.2 reconcile core, called DIRECTLY (never `resumeReceiverTransfer`, which would
          // re-acquire the ALREADY-HELD lock with `{ ifAvailable: true }` and skip — a
          // guaranteed bug). This owns the ENTIRE rest of the transfer's lifecycle.
          const record = await deps.p2p.getRecord();
          await reconcileReceiverAndContinue(record, manifest, deps.p2p.resumeDeps, true);
          return;
        }
        if (p2pOutcome.kind === "hashMismatch") {
          // §9.1 mismatch: DC/PC already closed WITHOUT a COMPLETE frame inside the P2P attempt
          // — run the EXISTING M2 HASH_MISMATCH terminal path verbatim via this same throw.
          throw new HashMismatchError();
        }
        if (p2pOutcome.kind === "completed") transport = "p2p";
        // "completed": fall through past the relay loop + hash check below — the P2P attempt
        // already sent the in-band COMPLETE frame (§9.1) and verified the hash internally; this
        // function's EXISTING completion tail (below the outer try/catch) still owns the wire
        // `complete` ctrl + onTerminal + finalize/download/delete, exactly as the relay path
        // does — and, unlike "demoted", this path never opens a second OPFS worker on this file
        // (the SAME `deps.output` handle is reused straight through to `finalize()`/`deleteCopy()`
        // below), so no `release()` call is needed here (MUST-2 review: verified no sibling leak).
      }

      if (p2pOutcome.kind !== "completed") {
        // Fresh-path availability gate (§6.2 step 7, mirrored from receiveResume.ts's
        // `waitForPart`): poll `partsUploaded` until the part exists before asking for its
        // download-url. Without it the first fetch races the sender's upload phase and dies
        // on 409 `part_not_uploaded` (prod 2026-08-16 — the sender had created the transfer
        // 16ms earlier and was still in its P2P attempt). Cadence: active 2s → idle 60s after
        // 5min without progress; #821 liveness gives up after 15min of zero new parts.
        const nowFn = deps.now ?? (() => Date.now());
        const sleepFn = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
        let latestParts = new Set<number>();
        let prevPartsSize = 0;
        let lastProgressAt = nowFn();
        const waitForPart = async (partIndex: number): Promise<void> => {
          if (latestParts.has(partIndex)) return;
          // Per-invocation failure budget, reset on every call — same scoping as
          // receiveResume.ts's `networkFailures`.
          let pollFailures = 0;
          // Deliberate divergence from receiveResume.ts's sleep-then-poll ordering: this path
          // polls FIRST, because right after accept the part often lands within the first
          // cadence window and a blind lead-in sleep would just add latency (it also lets the
          // all-parts-present case pass through with zero sleeps).
          for (;;) {
            if (deps.isAborted?.()) throw new ReceiveAbortedError();
            if (nowFn() - lastProgressAt >= FT_RECEIVE_LIVENESS_MS) {
              throw new PartWaitStalledError();
            }
            try {
              const poll = await deps.api.getTransfer(transferId);
              pollFailures = 0;
              latestParts = new Set(poll.partsUploaded);
              if (latestParts.size > prevPartsSize) {
                lastProgressAt = nowFn();
                prevPartsSize = latestParts.size;
              }
              if (latestParts.has(partIndex)) return;
            } catch (err) {
              // A definitive 404 means the transfer is gone server-side (expired/cancelled) —
              // never coming back, so stop polling. Transient failures ride the same cadence
              // below with a small budget (mirrors receiveResume.ts's networkFailures rule).
              if (isDefinitive404(err)) throw err;
              pollFailures++;
              if (pollFailures > 3) throw err;
            }
            const idle = nowFn() - lastProgressAt >= FT_POLL_IDLE_AFTER_MS;
            await sleepFn(idle ? FT_POLL_IDLE_MS : FT_POLL_ACTIVE_MS);
          }
        };
        // `runReceiveLoop` verifies EVERY file's whole-file hash internally, per-file, the
        // moment its own last chunk is written (see receiveLoop.ts's module doc) — a
        // mismatch on ANY file throws `HashMismatchError` here, aborting the whole (atomic)
        // transfer, exactly like the old single-file post-loop check.
        await runReceiveLoop({
          fetchPart: async (partIndex) => {
            await waitForPart(partIndex);
            const { url } = await deps.api.getDownloadUrl(transferId, partIndex);
            const stream = await deps.api.fetchPartStream(url);
            return { stream };
          },
          crypto: deps.crypto,
          hash: deps.hash,
          plan,
          files: manifest.files,
          outputFor,
          hashStateFor,
          transferKey,
          noncePrefix: noncePrefixBytes,
          transferId,
          chunkSize: manifest.chunkSize,
          partCount,
          onPartWritten: deps.onPartWritten,
          onProgressCtrl: sendProgressCtrl,
          onProgress: deps.onProgress,
          isAborted: deps.isAborted,
        });
      }
    } catch (err) {
      if (err instanceof ReceiveAbortedError) {
        // Cancel/unpair — no ctrl re-send (the canceller already knows, §8.3), no DELETE (receiver never owns the transfer registration).
        //
        // HOST CONTRACT — `onTerminal("cancelled")` MUST run before the rethrow. Both hosts
        // treat a rethrown `ReceiveAbortedError` as "already reported" and drop it silently:
        // apps/web's `startReceive` returns from its catch without logging (PWA-7 — logging it
        // filed every user cancel as a Sentry error), and the extension's offscreen host skips
        // its catch body via the `activeReceives.has` guard (`finish` deleted the entry here).
        // A NEW abort throw site that skips this call therefore makes the transfer vanish from
        // both UIs with no terminal at all. `__tests__/receiveHost.test.ts` pins the pairing at
        // both existing throw sites (the loop's `isAborted` and `waitForPart`); a new one needs
        // the same coverage.
        await deleteAllOutputs();
        await deps.onTerminal?.("cancelled", undefined, transport);
        throw err;
      }
      if (isNetworkError(err)) {
        // §7.3: a transport drop mid-download (a raw stream-read rejection or the
        // part-download deadline firing — both re-classified into TransferApiError(0)
        // at the ciphertextSplitter boundary) is NOT terminal. Leave the record at its
        // last "transferring" checkpoint and RETURN normally — no peer error ctrl, no
        // server DELETE, no `onTerminal`, and KEEP the partial output (deleteCopy would
        // discard it). The caller disposes; the resume scan (§6.1) re-downloads the rest
        // from R2. Returning (not throwing) mirrors the P2P-demote handoff above — a
        // non-terminal exit the offscreen/web caller already tolerates. Before this, a
        // network blip on the FIRST attempt fell through to INTERNAL and destroyed the
        // whole transfer (server delete + partial discarded) with a wrong wire code.
        // A network error only reaches here on the relay download path, so
        // `p2pOutcome` is never "completed" — the skipped complete/channel-close block
        // below has nothing to run.
        return;
      }
      const code =
        err instanceof HashMismatchError
          ? "HASH_MISMATCH"
          : err instanceof ChunkAuthError
            ? "CHUNK_AUTH_FAIL"
            : isQuotaExceededError(err)
              ? "STORAGE_FULL"
              : "INTERNAL";
      await deps.sendCtrl({ kind: "error", transferId, code, message: "" }).catch(() => {});
      await deleteAllOutputs();
      await deps.onTerminal?.("failed", code, transport);
      throw err;
    }

    await deps.sendCtrl({ kind: "complete", transferId, verified: true }).catch(() => {});
    try {
      await deps.onTerminal?.("complete", undefined, transport);
    } finally {
      // §9.1 pinned ordering: COMPLETE frame (already sent inside the P2P attempt) -> wire ctrl
      // (just sent above) -> P-R4 persist (the try body above, EVEN IF it throws) -> NOW close
      // DC/PC/session — the `finally` guarantees this still runs on an `onTerminal` throw, rather
      // than leaking the P2P channel/session open.
      if (p2pOutcome.kind === "completed") {
        p2pOutcome.closeChannel();
      }
    }

    // Finalize -> downloads handoff -> delete the OPFS copy ONLY after the
    // download completes (TASK-17 acceptance: "OPFS copy removed after
    // successful download handoff"), ONE FILE AT A TIME, in manifest order. A
    // downloads failure leaves that file's OPFS copy in place — the user's bytes
    // are already durably verified; deleting them on a failed handoff would be a
    // silent data loss, not cleanup — and propagates, so no LATER file's handoff
    // is attempted (matches the pre-multi-file "let it throw" contract).
    //
    // Each finalized OPFS file is re-wrapped with ITS OWN manifest entry's TRUE
    // name and MIME (hardware bug): OPFS files carry `type: ""`, and Chrome's
    // safe-filename pass then treats the blob download as text-like and rewrites
    // the saved extension to `.txt`. `new File([blob], ...)` references the
    // same bytes — no copy. `mime` is E2E manifest content; it never leaves the
    // client.
    for (let i = 0; i < manifest.files.length; i++) {
      const fileEntry = manifest.files[i];
      const finishedFile = await outputFor(i).finalize();
      // `rewrapFinalizedFile` (fileSeam.ts) owns the Blob-only re-wrap rule for BOTH receive tails
      // — see its doc for the #824 history (a path-carrying CLI carrier must pass through
      // untouched; an OPFS File must gain the manifest's name+mime).
      const typedFile = rewrapFinalizedFile(finishedFile, fileEntry);
      const disposition = await deps.downloads.download(typedFile, fileEntry.name);
      // Delete the staged copy ONLY on a confirmed handoff. A "detached" handoff
      // (web's anchor fallback) is still reading these bytes: an OPFS-backed File —
      // and a `new File([...])` re-wrap of it, which does NOT snapshot — throws
      // NotFoundError the instant the entry is removed, truncating the user's
      // download with no copy left to retry from. `gcOrphanTransfers` reclaims it
      // on the next load instead (the record is terminal, so it sweeps as an orphan).
      if (disposition === "durable") await outputFor(i).deleteCopy();
    }
  } finally {
    if (p2pLockHeld && deps.p2p) await deps.p2p.releaseLock(transferId);
  }
}

export type { ReceiveHandoffDisposition } from "./receiveLoop";
export { HashMismatchError } from "./receiveLoop";
